import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { GarageTermsStatus } from '../../types';
import { useDialogFocus } from '../config/ConfigHistoryDialog';
import TermsStatusBadge from './TermsStatusBadge';
import {
  RECIPIENT_KIND_LABEL,
  TERMS_NOTICE_SEND_SCREEN,
  formatUae,
  isSignedStatus,
  plural,
  recipientStatusBadge,
  termsNoticeErrorMessage,
} from './termsNotice';

interface TermsStatusCardProps {
  garageId: string;
  /** Called after a send and after a manual refresh, so the Signed agreements card can reload too. */
  onChanged?: () => void;
}

type Toast = { message: string; type: 'success' | 'error' };

/**
 * Spec 074 (#1145) — the garage's terms status (v2: Signed / Not signed; a delivered email notice reads
 * "Signed · via email notice"), the evidence behind it and the latest terms notice with every recipient's
 * delivery state. Holders of the `signed-agreements` screen can send the current terms to any garage
 * ("Send terms again" when it is Signed); the server re-checks the screen, the in-flight rule and the
 * Runtime Config switch.
 */
export default function TermsStatusCard({ garageId, onChanged }: TermsStatusCardProps) {
  const auth = useAuth();
  const canSend = auth.hasScreen(TERMS_NOTICE_SEND_SCREEN);
  const [data, setData] = useState<GarageTermsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = (message: string, type: Toast['type']) => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 5000);
  };

  const load = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setLoadError(null);
    api.getGarageTermsStatus(garageId, auth.token)
      .then(setData)
      .catch((err) => setLoadError(termsNoticeErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [garageId, auth.token]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    load();
    onChanged?.();
  };

  const handleSend = async () => {
    if (!auth.token) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await api.sendGarageTermsNotice(garageId, auth.token);
      setConfirming(false);
      const failed = result.failedRecipients > 0
        ? ` ${plural(result.failedRecipients, 'email')} could not be queued: send again to retry.`
        : '';
      showToast(`Terms sent to ${plural(result.recipients, 'email')}.${failed}`,
        result.failedRecipients > 0 ? 'error' : 'success');
      load();
      onChanged?.();
    } catch (err) {
      setConfirming(false);
      setSendError(termsNoticeErrorMessage(err));
      load();
    } finally {
      setSending(false);
    }
  };

  const signed = !!data && isSignedStatus(data.status);
  const showSend = !!data && !data.inFlight && data.noticesEnabled && canSend;
  const notice = data?.latestNotice ?? null;

  return (
    <div className="admin-card" data-testid="terms-status-card" style={{ marginBottom: '1.5rem' }}>
      <div className="terms-card-header">
        <h3 className="admin-card-title" style={{ margin: 0 }}>Terms status</h3>
        {!loading && !loadError && (
          <button type="button" className="admin-btn admin-btn-outline signed-agreements-btn" onClick={refresh}>
            Refresh
          </button>
        )}
      </div>

      {loading && !data ? (
        <p className="signed-agreements-muted">Loading terms status...</p>
      ) : loadError ? (
        <div className="admin-info-banner danger signed-agreements-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="admin-btn admin-btn-outline signed-agreements-btn" onClick={load}>
            Retry
          </button>
        </div>
      ) : data ? (
        <>
          <div className="terms-status-summary">
            <TermsStatusBadge status={data.status} testId="terms-status-badge" />
            <span className="terms-status-detail" data-testid="terms-status-detail">
              {data.status === 'SIGNED' && (
                <>
                  Signed by <strong dir="auto">{data.signerName || 'Not recorded'}</strong> on {formatUae(data.signedAt)}
                </>
              )}
              {data.status === 'NOTIFIED' && (
                <>
                  Signed · via email notice on {formatUae(data.notifiedAt)} to <strong>{data.recipient || '—'}</strong>
                </>
              )}
              {data.status === 'NOT_SIGNED' && 'No signature or delivered email notice for the current partner terms.'}
            </span>
          </div>

          {data.inFlight && (
            <div className="admin-info-banner info" role="status" style={{ marginTop: '1rem' }}>
              A notice is on its way. This card updates when the email provider reports delivery; use Refresh to check.
            </div>
          )}
          {!data.noticesEnabled && canSend && (
            <p className="signed-agreements-muted" style={{ marginTop: '0.75rem' }}>
              Sending is switched off in Runtime Config (garagat.terms.notices.enabled).
            </p>
          )}
          {sendError && (
            <div className="admin-info-banner danger" role="alert" style={{ marginTop: '1rem', marginBottom: 0 }}>
              {sendError}
            </div>
          )}

          {notice && (
            <div className="terms-notice-latest" data-testid="terms-latest-notice">
              <h4 className="terms-notice-heading">Latest notice</h4>
              <p className="signed-agreements-muted">
                Sent {formatUae(notice.createdAt)}
                {notice.requestedByName ? <> by <span dir="auto">{notice.requestedByName}</span></> : null}
              </p>
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <caption className="signed-agreements-sr-only">Latest notice recipients</caption>
                  <thead>
                    <tr>
                      <th scope="col">Email</th>
                      <th scope="col">Recipient</th>
                      <th scope="col">Status</th>
                      <th scope="col">Delivered at (UAE)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notice.recipients.map((r) => {
                      const rb = recipientStatusBadge(r.status);
                      return (
                        <tr key={`${r.email}-${r.kind}`} data-testid="terms-notice-recipient">
                          <td className="terms-notice-email">{r.email}</td>
                          <td>{RECIPIENT_KIND_LABEL[r.kind] ?? r.kind}</td>
                          <td>
                            <span className={`admin-badge ${rb.cls}`}>{rb.label}</span>
                            {r.error && <div className="terms-notice-error">{r.error}</div>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{r.deliveredAt ? formatUae(r.deliveredAt) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {showSend && (
            <div style={{ marginTop: '1rem' }}>
              <button
                type="button"
                className="admin-btn admin-btn-primary signed-agreements-btn"
                onClick={() => { setSendError(null); setConfirming(true); }}
                disabled={sending}
              >
                {signed ? 'Send terms again' : 'Send terms'}
              </button>
            </div>
          )}
        </>
      ) : null}

      {confirming && (
        <SendConfirmDialog
          signed={signed}
          sending={sending}
          onConfirm={() => void handleSend()}
          onCancel={() => { if (!sending) setConfirming(false); }}
        />
      )}

      {toast && <div className={`admin-toast ${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
}

function SendConfirmDialog({ signed, sending, onConfirm, onCancel }: {
  signed: boolean;
  sending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(cancelRef, onCancel);
  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="terms-send-title">
      <div className="admin-modal admin-modal-sm">
        <div className="admin-modal-header">
          <h3 id="terms-send-title">{signed ? 'Send terms again?' : 'Send terms?'}</h3>
        </div>
        <div className="admin-modal-body">
          <p style={{ marginTop: 0 }}>
            This emails the current partner terms as PDFs to the garage email and the owner&apos;s email.
          </p>
          <p style={{ marginBottom: 0 }}>
            {signed
              ? 'This garage is already Signed: it gets the email only, and its records stay as they are.'
              : <>The garage shows as <strong>Signed</strong> (via email notice) once an email is delivered.</>}
          </p>
        </div>
        <div className="admin-modal-footer">
          <button ref={cancelRef} type="button" className="admin-btn admin-btn-outline" onClick={onCancel} disabled={sending}>
            Cancel
          </button>
          <button type="button" className="admin-btn admin-btn-primary" onClick={onConfirm} disabled={sending} aria-busy={sending || undefined}>
            {sending ? 'Sending…' : 'Send terms'}
          </button>
        </div>
      </div>
    </div>
  );
}
